/**
 * Prediction hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { predictionsApi, specialsApi, ApiError } from '../lib/api';
import { useAppStore } from '../stores/appStore';
import type { Prediction, SpecialBet, SpecialBetWithPrediction, TrendDirection } from '../../shared/types';

// Query keys
export const predictionKeys = {
  all: ['predictions'] as const,
  byPool: (slug: string) => [...predictionKeys.all, 'pool', slug] as const,
  byMatch: (slug: string, matchId: number) => [...predictionKeys.all, 'match', slug, matchId] as const,
  stats: (slug: string) => [...predictionKeys.all, 'stats', slug] as const,
  specials: () => ['specials'] as const,
  specialsByPool: (slug: string) => [...predictionKeys.specials(), 'pool', slug] as const,
};

/**
 * Hook to get my predictions in a pool
 */
export function useMyPredictions(poolSlug: string) {
  return useQuery({
    queryKey: predictionKeys.byPool(poolSlug),
    queryFn: async () => {
      const response = await predictionsApi.getMyPredictions(poolSlug);
      return response.data as Prediction[];
    },
    enabled: !!poolSlug,
  });
}

/**
 * Hook to get all predictions for a match (after lock time)
 */
export function useMatchPredictions(poolSlug: string, matchId: number) {
  return useQuery({
    queryKey: predictionKeys.byMatch(poolSlug, matchId),
    queryFn: async () => {
      const response = await predictionsApi.getMatchPredictions(poolSlug, matchId);
      return response.data as Prediction[];
    },
    enabled: !!poolSlug && !!matchId,
  });
}

/**
 * Hook to get my stats in a pool
 */
export function useMyStats(poolSlug: string) {
  return useQuery({
    queryKey: predictionKeys.stats(poolSlug),
    queryFn: async () => {
      const response = await predictionsApi.getMyStats(poolSlug);
      return response.data as {
        position: number | null;
        totalPoints: number;
        exactScores: number;
        predictionsCount: number;
        totalMatches: number;
        pendingPredictions?: number;
        trend?: TrendDirection | 'new' | 'same' | 'up' | 'down';
      };
    },
    enabled: !!poolSlug,
  });
}

/**
 * Hook to create or update a prediction.
 *
 * Pass `silent: true` in the payload to suppress the global toast (used by
 * autosave flows that surface per-match status inline).
 */
export function useSubmitPrediction(poolSlug: string) {
  const queryClient = useQueryClient();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: ({
      matchId,
      homeScore,
      awayScore,
    }: {
      matchId: number;
      homeScore: number;
      awayScore: number;
      silent?: boolean;
    }) => predictionsApi.createOrUpdate(poolSlug, matchId, homeScore, awayScore),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: predictionKeys.byPool(poolSlug) });
      queryClient.invalidateQueries({ queryKey: predictionKeys.stats(poolSlug) });
      if (variables.silent) return;
      addNotification({
        type: 'success',
        message: response.data?.message || 'Palpite registrado!',
        duration: 3000,
      });
    },
    onError: (error: ApiError, variables) => {
      if (variables.silent) return;
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao registrar palpite',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook to get all special bet types
 */
export function useSpecialBets() {
  return useQuery({
    queryKey: predictionKeys.specials(),
    queryFn: async () => {
      const response = await specialsApi.list();
      return response.data as SpecialBet[];
    },
  });
}

/**
 * Hook to get my special predictions in a pool
 */
export function useMySpecialPredictions(poolSlug: string) {
  return useQuery({
    queryKey: predictionKeys.specialsByPool(poolSlug),
    queryFn: async () => {
      const response = await specialsApi.getMyPredictions(poolSlug);
      return response.data as SpecialBetWithPrediction[];
    },
    enabled: !!poolSlug,
  });
}

/**
 * Hook to submit a special prediction
 */
export function useSubmitSpecialPrediction(poolSlug: string) {
  const queryClient = useQueryClient();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: ({ betId, prediction }: { betId: number; prediction: string }) =>
      specialsApi.createOrUpdate(poolSlug, betId, prediction),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: predictionKeys.specialsByPool(poolSlug) });
      addNotification({
        type: 'success',
        message: response.data?.message || 'Palpite especial registrado!',
        duration: 3000,
      });
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao registrar palpite especial',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook to set correct answer for a special bet (admin only)
 */
export function useSetSpecialBetAnswer() {
  const queryClient = useQueryClient();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: ({ betId, correctAnswer }: { betId: number; correctAnswer: string }) =>
      specialsApi.setCorrectAnswer(betId, correctAnswer),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: predictionKeys.specials() });
      queryClient.invalidateQueries({ queryKey: ['pools', 'ranking'] });
      addNotification({
        type: 'success',
        message: response.data?.message || 'Resposta correta definida!',
        duration: 3000,
      });
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao definir resposta',
        duration: 5000,
      });
    },
  });
}
